import { _ } from "../../../core/polyglot/polyglot.js";
import {
	circleTimesIcon,
	downloadIcon,
	editIcon,
	envelopeIcon,
	eyeIcon,
	eyeSlashIcon,
	folderIcon,
	maximizeIcon,
	uploadIcon,
	wrenchIcon,
} from "../../../core/elements/icons/index.js";
import { iconAndText } from "../../../core/elements/index.js";
import commandVisitor from "./commandVisitor.js";
import FileUploadButtonWidget from "../../../core/widgets/FileUploadButtonWidget.js";
import notifier from "../../../core/widgets/notifier/notifier.js";
import UploadService from "../../../core/services/UploadService.js";

/**
 * `commandWidgetBuilderVisitor` is a concrete implementation of
 * `commandVisitor' used to build widgets for commands.
 */

/**
 * Todo: Add JSDoc comment
 */
function commandWidgetBuilderVisitor() {
	const that = commandVisitor();

	that.visitCreateDirectoryCommand = function (aCommand) {
		return buildButtonFor({
			command: aCommand,
			icon: folderIcon,
			name: _("Create directory"),
			dataTestName: "create-directory",
			dataHelpName: "create-directory",
		});
	};

	that.visitDeleteCommand = function (aCommand) {
		return buildButtonFor({
			command: aCommand,
			icon: circleTimesIcon,
			name: _("Delete"),
			dataTestName: "delete",
		});
	};

	that.visitDownloadCommand = function (aCommand) {
		return buildButtonFor({
			command: aCommand,
			icon: downloadIcon,
			name: _("Download"),
			dataTestName: "download",
		});
	};

	that.visitExportCommand = function (aCommand) {
		return buildButtonFor({
			command: aCommand,
			icon: downloadIcon,
			name: _("Export archive"),
		});
	};

	that.visitMarkAsPreliminaryCommand = function (aCommand) {
		const icon = aCommand.isPreliminary() ? eyeIcon : eyeSlashIcon;
		const title = aCommand.isPreliminary()
			? _("Mark as permanent")
			: _("Mark as preliminary");

		return buildButtonFor({ command: aCommand, icon, name: title });
	};

	that.visitEditEntryPermissionsCommand = function (aCommand) {
		return buildButtonFor({
			command: aCommand,
			icon: wrenchIcon,
			name: _("Permissions"),
			dataTestName: "permissions",
		});
	};

	that.visitMoveCommand = function (aCommand) {
		return buildButtonFor({
			command: aCommand,
			icon: maximizeIcon,
			name: _("Move"),
			dataTestName: "move",
		});
	};

	that.visitRenameCommand = function (aCommand) {
		return buildButtonFor({
			command: aCommand,
			icon: editIcon,
			name: _("Rename"),
			dataTestName: "rename",
		});
	};

	that.visitUploadCommand = function (aCommand) {
		return buildUploadButtonFor({
			command: aCommand,
			name: _("Upload"),
			dataHelpName: "upload",
			cssClass: "btn-primary",
			validate: (file) => {
				if (aCommand.getFolder().hasChildWithName(file.name)) {
					notifier.error(
						_(
							"This name is already used by a file or a folder in this directory",
						),
					);

					return false;
				}

				return true;
			},
		});
	};

	that.visitImportCommand = function (aCommand) {
		return buildUploadButtonFor({
			command: aCommand,
			name: _("Import archive"),
			cssClass: "btn-default",
		});
	};

	that.visitAgencyImportCommand = function (aCommand) {
		return buildButtonFor({
			command: aCommand,
			icon: uploadIcon,
			name: _("Import archive from agency"),
		});
	};

	that.visitNotifyUsersCommand = function (aCommand) {
		return buildButtonFor({
			command: aCommand,
			icon: envelopeIcon,
			name: _("Send mail notification"),
			dataTestName: "notification",
		});
	};

	function buildUploadButtonFor({
		command,
		name,
		cssClass,
		validate,
		dataHelpName,
	}) {
		const uploadService = new UploadService({
			uploadPath: command.uploadPath(),
		});

		return new FileUploadButtonWidget({
			label: iconAndText(
				{ icon: uploadIcon({ dataTest: "upload" }) },
				name,
			),
			cssClass: `btn btn-block ${cssClass}`,
			onUpload: uploadService.uploadFile.bind(uploadService),
			onSuccess: (file) => {
				command.setName(file.name);
				command.perform();
			},
			onError: ({ error }) => {
				notifier.error(error.message);
			},
			validate,
			dataHelpName,
		});
	}

	function buildButtonFor({
		command,
		icon,
		name,
		cssClass,
		dataTestName = "",
		dataHelpName = "",
	}) {
		return (html) =>
			html.button(
				{
					"data-test": dataTestName,
					"data-help": dataHelpName,
					klass: ["btn", "btn-block", cssClass || "btn-default"],
					click: command.perform.bind(command),
				},
				icon,
				html.span(` ${name}`),
			);
	}

	return that;
}

export default commandWidgetBuilderVisitor;
